import type { AuditEntry } from "../domain/types";
import { STEP_CONFIG, STATUS_CONFIG } from "../../validation/domain/types";
import type {
  ValidationStep,
  ValidationStatus,
} from "../../validation/domain/types";

function isValidationStep(v: unknown): v is ValidationStep {
  return v === "step_1" || v === "step_2" || v === "step_3";
}
function isValidationStatus(v: unknown): v is ValidationStatus {
  return v === "pending" || v === "validated" || v === "rejected";
}

export function AuditTrailSection({
  auditTrail,
}: {
  auditTrail: AuditEntry[];
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {auditTrail.length === 0
            ? "No changes yet"
            : `${auditTrail.length} change${auditTrail.length > 1 ? "s" : ""}`}
        </span>
      </div>
      <div className="max-h-[320px] overflow-y-auto">
        {auditTrail.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-sm font-medium text-slate-600">
              No manual overrides yet.
            </p>
            <p className="mt-1 text-sm text-slate-400">
              When data is available, use the pencil icon on a cell to create an
              audit-tracked override.
            </p>
          </div>
        ) : (
          auditTrail.map((entry) => {
            const isValidation =
              entry.eventLabel?.startsWith("validation_step");
            if (
              isValidation &&
              isValidationStep(entry.fieldKey) &&
              isValidationStatus(entry.nextValue)
            ) {
              const step: ValidationStep = entry.fieldKey;
              const status: ValidationStatus = entry.nextValue;
              const stepCfg = STEP_CONFIG[step];
              const statusCfg = STATUS_CONFIG[status];
              return (
                <div
                  key={entry.id}
                  className="grid grid-cols-[1fr_auto] gap-4 border-b border-slate-100 px-4 py-3 text-xs last:border-b-0"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      {stepCfg && (
                        <span className={`font-semibold ${stepCfg.textColor}`}>
                          {stepCfg.label}
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${statusCfg.badgeClass}`}
                      >
                        {statusCfg.icon} {statusCfg.label}
                      </span>
                    </div>
                    <p className="text-slate-500">
                      by {entry.user}
                      {entry.comment ? ` — ${entry.comment}` : ""}
                    </p>
                  </div>
                  <div className="whitespace-nowrap text-right text-slate-400">
                    {new Date(entry.updatedAt).toLocaleString()}
                  </div>
                </div>
              );
            }
            return (
              <div
                key={entry.id}
                className="grid grid-cols-[1fr_auto] gap-4 border-b border-slate-100 px-4 py-3 text-xs last:border-b-0"
              >
                <div>
                  <p className="font-semibold text-slate-700">
                    {entry.companyName}
                  </p>
                  {entry.eventLabel === "false_positive" ? (
                    <>
                      <p className="mt-0.5 text-slate-600">
                        False positive — {entry.fieldLabel}
                      </p>
                      {(entry.trrValue ||
                        entry.pdfValue ||
                        entry.goldenValue) && (
                        <div className="mt-1.5 grid grid-cols-3 gap-1 rounded-md bg-slate-50 px-2 py-1.5 text-[11px]">
                          <span>
                            <span className="font-medium text-amber-700">
                              Golden{" "}
                            </span>
                            <span className="text-slate-600">
                              {entry.goldenValue || "—"}
                            </span>
                          </span>
                          <span>
                            <span className="font-medium text-emerald-700">
                              XLSX{" "}
                            </span>
                            <span className="text-slate-600">
                              {entry.trrValue || "—"}
                            </span>
                          </span>
                          <span>
                            <span className="font-medium text-rose-700">
                              PDF{" "}
                            </span>
                            <span className="text-slate-600">
                              {entry.pdfValue || "—"}
                            </span>
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="mt-0.5 text-slate-600">
                      {entry.fieldLabel}:{" "}
                      <span className="line-through text-slate-400">
                        {entry.previousValue}
                      </span>{" "}
                      <span className="font-semibold text-violet-700">
                        {entry.nextValue}
                      </span>
                    </p>
                  )}
                  <p className="mt-0.5 text-slate-500">
                    {entry.eventLabel} by {entry.user}
                    {entry.comment ? ` — ${entry.comment}` : ""}
                  </p>
                </div>
                <div className="whitespace-nowrap text-right text-slate-400">
                  {new Date(entry.updatedAt).toLocaleString()}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
